import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import LoadingOverlay from "../../components/UI/LoadingOverlay/LoadingOverlay";
import styles from "./ResetPasswordPage.module.css";
import SoftindexLogo from "../../assets/images/softindex-website-logo.webp";

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
    isValid: false,
  });
  const { resetPassword, validateResetToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
      setValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        await validateResetToken(token);
        setValidating(false);
      } catch (err) {
        console.error("Token validation error:", err);
        setError(
          err.message ||
            "Invalid or expired reset link. Please request a new password reset."
        );
        setValidating(false);
      }
    };

    validateToken();
  }, [token, validateResetToken]);

  const checkPasswordStrength = (pwd) => {
    const feedback = [];
    let score = 0;

    if (pwd.length >= 8) {
      score += 1;
    } else {
      feedback.push("At least 8 characters");
    }

    if (/[a-z]/.test(pwd)) {
      score += 1;
    } else {
      feedback.push("At least one lowercase letter");
    }

    if (/[A-Z]/.test(pwd)) {
      score += 1;
    } else {
      feedback.push("At least one uppercase letter");
    }

    if (/[0-9]/.test(pwd)) {
      score += 1;
    } else {
      feedback.push("At least one number");
    }

    if (/[^A-Za-z0-9]/.test(pwd)) {
      score += 1;
    } else {
      feedback.push("At least one special character");
    }

    return {
      score,
      feedback,
      isValid: score >= 4 && pwd.length >= 8,
    };
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password.trim()) {
      setError("Please enter a password");
      return;
    }

    if (!passwordStrength.isValid) {
      setError("Password does not meet strength requirements");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      console.error("Reset password error:", err);
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/auth");
  };

  const handleRequestNewLink = () => {
    navigate("/forgot-password");
  };

  if (validating) {
    return (
      <LoadingOverlay
        isVisible={true}
        message="Validating reset link..."
        maxDisplayTime={500}
      />
    );
  }

  if (success) {
    return (
      <div className={styles.authPage}>
        <div className={styles.container}>
          <div className={styles.logoSection}>
            <img
              src={SoftindexLogo}
              alt="Softindex Logo"
              className={styles.logo}
            />
          </div>

          <div className={styles.content}>
            <div className={styles.successIcon}>
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22,4 12,14.01 9,11.01" />
              </svg>
            </div>

            <h1 className={styles.title}>Password Reset Successfully!</h1>
            <p className={styles.subtitle}>
              Your password has been updated. You can now sign in with your new
              password.
            </p>

            <button onClick={handleBackToLogin} className={styles.loginButton}>
              Sign In Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.authPage}>
      <div className={styles.container}>
        <div className={styles.logoSection}>
          <img
            src={SoftindexLogo}
            alt="Softindex Logo"
            className={styles.logo}
          />
        </div>

        <div className={styles.content}>
          <h1 className={styles.title}>Reset Your Password</h1>
          <p className={styles.subtitle}>
            Enter your new password below. Make sure it&apos;s strong and
            secure.
          </p>

          {error && (
            <div className={styles.errorMessage}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={styles.errorIcon}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <label className={styles.label}>New Password</label>
              <div className={styles.passwordInputContainer}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={password}
                  onChange={handlePasswordChange}
                  className={styles.passwordInput}
                  disabled={loading}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.passwordToggle}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>

              {password && (
                <div className={styles.passwordStrength}>
                  <div className={styles.strengthBar}>
                    <div
                      className={`${styles.strengthFill} ${
                        passwordStrength.score >= 1 ? styles.weak : ""
                      } ${passwordStrength.score >= 3 ? styles.medium : ""} ${
                        passwordStrength.score >= 4 ? styles.strong : ""
                      }`}
                      style={{
                        width: `${(passwordStrength.score / 5) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <div className={styles.strengthText}>
                    {passwordStrength.score < 2 && "Weak"}
                    {passwordStrength.score >= 2 &&
                      passwordStrength.score < 4 &&
                      "Medium"}
                    {passwordStrength.score >= 4 && "Strong"}
                  </div>
                </div>
              )}

              {passwordStrength.feedback.length > 0 && (
                <div className={styles.passwordFeedback}>
                  <ul>
                    {passwordStrength.feedback.map((item, index) => (
                      <li key={index} className={styles.feedbackItem}>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>Confirm Password</label>
              <div className={styles.passwordInputContainer}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={styles.passwordInput}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={styles.passwordToggle}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={
                !password.trim() ||
                !confirmPassword.trim() ||
                !passwordStrength.isValid ||
                loading
              }
              className={styles.submitButton}
            >
              {loading ? (
                <div className={styles.spinner}></div>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>

          <div className={styles.helpText}>
            <p>
              Remember your password?{" "}
              <button
                onClick={handleBackToLogin}
                className={styles.loginLink}
                disabled={loading}
              >
                Sign in here
              </button>
            </p>
            <p>
              Need a new reset link?{" "}
              <button
                onClick={handleRequestNewLink}
                className={styles.resetLink}
                disabled={loading}
              >
                Request another one
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
